import React, { useEffect, useState, useCallback } from 'react';
import { sb } from '../../services/supabase';
import { PageHeader, Card, Table, Td, EmptyRow, Btn, Modal, FormField, Input, useToast, Badge, StatCard } from '../../components/ui';

export default function KioskManagement({ companyId }: { companyId: string }) {
  const toast = useToast();
  const [devices, setDevices] = useState<any[]>([]);
  const [modal, setModal] = useState(false);
  const [qrModal, setQrModal] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ device_name: '', pin: '' });

  const load = useCallback(async () => {
    const { data } = await sb.from('kiosk_devices').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
    setDevices(data || []);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const generatePin = () => setForm(f => ({ ...f, pin: Math.floor(100000 + Math.random() * 900000).toString() }));

  const save = async () => {
    if (!form.device_name || !form.pin) { toast('Device name and PIN required', 'error'); return; }
    if (form.pin.length < 4) { toast('PIN must be at least 4 digits', 'error'); return; }
    setSaving(true);
    const { error } = await sb.from('kiosk_devices').insert({ company_id: companyId, device_id: 'pending', device_name: form.device_name, pin: form.pin });
    setSaving(false);
    if (error) { toast(error.message, 'error'); return; }
    toast('✅ Kiosk created!', 'success'); setModal(false); setForm({ device_name: '', pin: '' }); load();
  };

  const toggle = async (id: string, isActive: boolean) => {
    await sb.from('kiosk_devices').update({ is_active: !isActive }).eq('id', id);
    toast(isActive ? 'Deactivated' : 'Activated', 'success'); load();
  };

  const del = async (id: string) => {
    if (!window.confirm('Delete this kiosk device?')) return;
    await sb.from('kiosk_devices').delete().eq('id', id);
    toast('Deleted', 'success'); load();
  };

  return (
    <div className="animate-fadeUp" style={{ padding: 32 }}>
      <PageHeader title="Kiosk Devices" subtitle="Manage shared tablets for employee check-in" action={<Btn onClick={() => setModal(true)}>+ Add Kiosk Device</Btn>} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Devices" value={devices.length} color="var(--accent)" />
        <StatCard label="Active" value={devices.filter(d => d.is_active).length} color="var(--accent)" />
        <StatCard label="Registered" value={devices.filter(d => d.device_id !== 'pending').length} color="#845EC2" />
      </div>

      <div style={{ background: '#00C9A710', border: '1px solid #00C9A730', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'var(--accent)', lineHeight: 1.8 }}>
        <strong>How it works:</strong> Create a kiosk entry → share PIN or QR with tablet operator → they open ClockedIQ on tablet → Kiosk Mode → enter PIN or scan QR → device locks to that tablet permanently.
      </div>

      <Card>
        <Table headers={['Device Name', 'PIN', 'Device ID', 'Last Used', 'Status', 'Actions']}>
          {devices.length === 0 ? <EmptyRow cols={6} message="No kiosk devices yet" /> :
           devices.map(d => (
            <tr key={d.id}>
              <Td><div style={{ fontWeight: 600 }}>{d.device_name}</div><div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{d.id.slice(0,8)}...</div></Td>
              <Td><span style={{ fontFamily: 'var(--mono)', fontSize: 20, fontWeight: 800, color: 'var(--accent)', letterSpacing: 2 }}>{d.pin}</span></Td>
              <Td>{d.device_id === 'pending' ? <Badge type="yellow">Not registered</Badge> : <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{d.device_id.slice(0,20)}...</span>}</Td>
              <Td mono>{d.last_used_at ? new Date(d.last_used_at).toLocaleString('en-IN') : '—'}</Td>
              <Td><Badge type={d.is_active ? 'green' : 'red'}>{d.is_active ? 'active' : 'inactive'}</Badge></Td>
              <Td>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn size="sm" onClick={() => setQrModal(d)}>📱 QR</Btn>
                  <Btn variant="outline" size="sm" onClick={() => toggle(d.id, d.is_active)}>{d.is_active ? 'Disable' : 'Enable'}</Btn>
                  <Btn variant="danger" size="sm" onClick={() => del(d.id)}>Delete</Btn>
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      </Card>

      {modal && (
        <Modal title="Add Kiosk Device" onClose={() => setModal(false)}>
          <div style={{ display: 'grid', gap: 14 }}>
            <FormField label="Device Name"><Input value={form.device_name} onChange={e => setForm(f => ({...f, device_name: e.target.value}))} placeholder="Workshop Tablet, Reception Kiosk..." /></FormField>
            <FormField label="PIN (4-6 digits)">
              <div style={{ display: 'flex', gap: 10 }}>
                <Input value={form.pin} onChange={e => setForm(f => ({...f, pin: e.target.value.replace(/\D/g,'').slice(0,6)}))} placeholder="e.g. 4455" style={{ flex: 1 }} />
                <Btn variant="outline" onClick={generatePin}>🎲 Generate</Btn>
              </div>
            </FormField>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, fontSize: 12, color: 'var(--muted)', lineHeight: 1.7 }}>
              💡 The device ID is registered automatically when the tablet first connects. After that, only that tablet can use this PIN.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="outline" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn onClick={save} disabled={saving}>{saving ? 'Creating...' : 'Create Kiosk'}</Btn>
          </div>
        </Modal>
      )}

      {qrModal && (
        <Modal title={`${qrModal.device_name} — Kiosk Details`} onClose={() => setQrModal(null)}>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            {/* QR code using a free API */}
            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(JSON.stringify({ c: companyId, n: qrModal.device_name, p: qrModal.pin, i: qrModal.id }))}&color=00C9A7&bgcolor=080810`} alt="QR Code" style={{ borderRadius: 16, width: 250, height: 250 }} />
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>Or enter PIN manually:</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 36, fontWeight: 800, color: 'var(--accent)', letterSpacing: 8 }}>{qrModal.pin}</div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 16, lineHeight: 1.7 }}>
              Open ClockedIQ on the tablet → Kiosk Mode → Scan QR or enter PIN
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <Btn onClick={() => setQrModal(null)}>Done</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
